// Buildings & projects: types, contract kinds, stage look, and which contracts apply to a building.
import { STAGES } from './format';

export type Project = { id: string; name: string; code: string | null; status: string | null; sort: number | null; deleted_at: string | null };

export type Building = {
  id: string;
  site_id: string;
  project_id: string | null;
  name: string;
  short_code: string | null;
  aliases: string[];
  stage: string;
  pc_date: string | null;
  revised_pc_date: string | null;
  drive_folder_url: string | null;
  photos_url: string | null;
  notes: string | null;
  sort: number | null;
  updated_at: string;
  deleted_at: string | null;
};

export type ContractKind = 'main' | 'building' | 'subcontract' | 'consultant';

export type Contract = {
  id: string;
  site_id: string;
  kind: ContractKind;
  title: string | null;
  contract_no: string | null; // client ref (budget no.)
  project_no: string | null; // contractor's ref
  building_id: string | null;
  building_ids: string[];
  covers_all: boolean;
  has_claims: boolean;
  contractor_org_id: string | null;
  architect_org_id: string | null;
  sum_vep: number | null;
  deposit_pct: number | null;
  deposit_recovery_pct: number | null;
  retention_pct: number | null;
  retention_limit_pct: number | null;
  dlp_months: number | null;
  employer_name: string | null;
  works_name: string | null;
  cert_code: string | null;
  open_after_claim: number | null;
  open_gross_vep: number | null;
  open_variations_vep: number | null;
  open_deposit_recovered: number | null;
  open_retention_held: number | null;
  open_paid_vep: number | null;
  signed_on: string | null;
  status: string | null;
  notes: string | null;
  updated_at: string;
  deleted_at: string | null;
};

export const KINDS: { value: ContractKind; label: string; short: string; color: string; help: string }[] = [
  { value: 'main', label: 'Main / master contract', short: 'Main', color: '#1A1A1A', help: 'Over-arching contract shared by several buildings' },
  { value: 'building', label: 'Building contract', short: 'Building', color: '#A3470D', help: 'The contract for this building — claims run against it' },
  { value: 'subcontract', label: 'Subcontract', short: 'Subcontract', color: '#0F6B6B', help: 'e.g. MEP (WFM), trenching' },
  { value: 'consultant', label: 'Consultant / designer', short: 'Consultant', color: '#1E5A8A', help: 'Architect, engineer, MEP consultant, designer' },
];
export const kindOf = (k: string | null | undefined) => KINDS.find((x) => x.value === k) ?? KINDS[1];

export const STAGE_LOOK: Record<string, { color: string; tint: string; i: number }> = {
  design: { color: '#3D3730', tint: '#F0ECE6', i: 0 },
  mobilising: { color: '#1E5A8A', tint: '#EEF4F9', i: 1 },
  construction: { color: '#A3470D', tint: '#FBEBDD', i: 2 },
  handover: { color: '#7A5200', tint: '#F7F1E6', i: 3 },
  defects_liability: { color: '#0F6B6B', tint: '#EDF5F4', i: 4 },
  complete: { color: '#285C36', tint: '#E8F2EA', i: 5 },
  on_hold: { color: '#7A350A', tint: '#F6E6DC', i: -1 },
};
export const STAGE_STEPS = STAGES.filter((s) => s.value !== 'on_hold');

/** Contracts that apply to a building: listed on it, or covering every building of its project. */
export function contractsFor(b: Pick<Building, 'id' | 'project_id'>, all: Contract[], buildings: Pick<Building, 'id' | 'project_id'>[]): Contract[] {
  return all.filter((c) => {
    if (c.deleted_at) return false;
    if ((c.building_ids ?? []).includes(b.id)) return true;
    if (!c.covers_all) return false;
    // "all buildings in the project": the project of the contract's first listed building (or any, when none listed)
    const first = buildings.find((x) => x.id === (c.building_ids ?? [])[0]);
    return !first || first.project_id === b.project_id;
  });
}

/** Contract refs as shown: client ref (budget no.) · contractor's ref. */
export function contractRef(c: Pick<Contract, 'contract_no' | 'project_no'>): string {
  return [c.contract_no, c.project_no].filter(Boolean).join(' · ') || 'no ref yet';
}

/** "Treehouse, Sunset Villa + 3 more" */
export function coversText(c: Pick<Contract, 'building_ids' | 'covers_all'>, names: Map<string, string>, max = 2): string {
  if (c.covers_all) return 'All buildings in the project';
  const ns = (c.building_ids ?? []).map((id) => names.get(id) ?? '?');
  if (!ns.length) return 'No building yet';
  return ns.length > max ? `${ns.slice(0, max).join(', ')} + ${ns.length - max} more` : ns.join(', ');
}

export const DOC_TYPES: { value: string; label: string; help: string; toContract?: boolean }[] = [
  { value: 'contract', label: 'Building contract', help: 'Signed contract for this building — sets the money terms', toContract: true },
  { value: 'main_contract', label: 'Main / master contract', help: 'Over-arching contract shared by several buildings', toContract: true },
  { value: 'proposal', label: 'Contractor proposal', help: 'Quote or tender — scope, price, programme' },
  { value: 'addendum', label: 'Addendum or variation', help: 'Changes an existing contract', toContract: true },
  { value: 'subcontract', label: 'Subcontract', help: 'e.g. MEP (WFM), trenching', toContract: true },
  { value: 'consultant', label: 'Consultant agreement', help: 'Architect, engineer, MEP consultant, designer', toContract: true },
  { value: 'boq', label: 'BOQ / pricing', help: 'Bill of quantities or fee schedule' },
  { value: 'drawings', label: 'Drawings list / set', help: 'Kept with the building documents' },
  { value: 'other', label: 'Other document', help: 'Stored on the building only' },
];
export const docTypeLabel = (v: string | null | undefined) => DOC_TYPES.find((d) => d.value === v)?.label ?? 'Document';
